import { MessageType, AndroidActivityManagement, ITaskDetailsResponse, TaskMemoryUsage, TaskModule, TaskThread, TaskError } from 'app/modules/remote-control-ng/definitions';
import { MemoryBuffer } from '../../utils/memoryBuffer';
import { BaseMessage } from '../baseMessage';
import { hd } from '@nx-fe/services';

export interface ITaskDetailsAndroid {
    pid?: number,
    messageType?: (AndroidActivityManagement.AAM_GET_PROCESS_DETAILS | AndroidActivityManagement.AAM_GET_PROCESSES_LIST)
}

export class BaseTaskMessage extends BaseMessage {
    protected static requestIdToType = new Map<number, number>();
}

/** PC_ANDROID_ACTIVITY_MANAGEMENT MessageType, AAM_GET_PROCESS_DETAILS or AAM_GET_PROCESSES_LIST subcommands,
 * passed as a messageType option in the constructor
 */
export class TaskDetailsAndroidMessage extends BaseTaskMessage implements ITaskDetailsAndroid {
    pid: number;
    public responseTasks?: hd.IRcTask[];
    public responseDetails?: ITaskDetailsResponse;
    messageType: (AndroidActivityManagement.AAM_GET_PROCESS_DETAILS | AndroidActivityManagement.AAM_GET_PROCESSES_LIST);

    constructor(options?: Partial<ITaskDetailsAndroid>) {
        super();
        this.header.command = MessageType.PC_ANDROID_ACTIVITY_MANAGEMENT;
        if (options) {
            Object.assign(this, options);
            if (this.messageType === undefined) {
                throw new Error('Unknown TaskDetailsAndroidMessage subtype');
            }
        }
    }

    public serializeBody(buffer: MemoryBuffer): void {
        BaseTaskMessage.requestIdToType.set(this.reqId, this.messageType);

        buffer.writeUInt8(this.messageType);
        buffer.writeUInt32BE(this.pid);
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        if (this.header.errorCode != 0) {
            return;
        }
        if (this.reqId && BaseTaskMessage.requestIdToType.has(this.reqId)) {
            this.messageType = BaseTaskMessage.requestIdToType.get(this.reqId);
            BaseTaskMessage.requestIdToType.delete(this.reqId);
        } else {
            BaseTaskMessage.requestIdToType.delete(this.reqId);
            throw new Error('Unknown TaskDetailsAndroidMessage subtype');
        }

        if (this.messageType === AndroidActivityManagement.AAM_GET_PROCESSES_LIST) {
            this.responseTasks = this.deserializeList(buffer);
        }
        else if (this.messageType === AndroidActivityManagement.AAM_GET_PROCESS_DETAILS) {
            this.responseDetails = this.deserializeDetails(buffer);
        }
    }

    private deserializeList(buffer: MemoryBuffer): hd.IRcTask[] {
        const numOfProcesses = buffer.readUInt32BE();
        const bytesLeft = buffer.readUInt32BE();

        const jsonArray: hd.IRcTask[] = [];
        const count = (buffer.getByteArray().length - buffer.getPosition());
        if (count === bytesLeft) {
            for (let i = 0; i < numOfProcesses; i++) {
                let json = <hd.IRcTask>{};
                json.pid = buffer.readUInt32LE();
                json.uid = buffer.readUInt32LE();
                json.name = buffer.readCharArray(128);
                json.memory = buffer.readUInt32LE();
                jsonArray.push(json);
            }
        }
        return jsonArray;
    }

    private deserializeDetails(buffer: MemoryBuffer): ITaskDetailsResponse {
        let jsonArrayPackages = [];
        let numOfPackage = buffer.readUInt32BE();
        //var sizeOfPackage = buffer.readUInt32BE();

        for (let i = 0; i < numOfPackage; i++) {
            jsonArrayPackages.push({
                name: buffer.readString()
            });
            // console.log("tid: " + json.tid + "; pri: " + json.pri);
        }
        let jsonMem = new TaskMemoryUsage();
        jsonMem.dalvikVM = buffer.readUInt32BE();
        jsonMem.native = buffer.readUInt32BE();
        jsonMem.other = buffer.readUInt32BE();

        let jsonErr = new TaskError();
        jsonErr.condition = buffer.readUInt32BE();
        jsonErr.cause = buffer.readString();
        jsonErr.conditionMessage = buffer.readString();
        return {
            tasks: jsonArrayPackages,
            mem: jsonMem,
            err: jsonErr
        } as ITaskDetailsResponse;
    }
}

/** PC_ANDROID_ACTIVITY_MANAGEMENT MessageType, AAM_KILL_PROCESS subcommand */
export class KillAndroidProcessMessage extends BaseTaskMessage {
    private pid: number;
    constructor(pid: number) {
        super();
        this.header.command = MessageType.PC_ANDROID_ACTIVITY_MANAGEMENT;
        this.pid = pid;
    }

    public serializeBody(buffer: MemoryBuffer): void {
        BaseTaskMessage.requestIdToType.set(this.reqId, AndroidActivityManagement.AAM_KILL_PROCESS);

        buffer.writeUInt8(AndroidActivityManagement.AAM_KILL_PROCESS);
        buffer.writeUInt32BE(this.pid);
    }
}

export interface ITaskDetailsWinResponse {
    threads: TaskThread[],
    modules: TaskModule[]
}

/** PC_GETTHRANDMODLISTS MessageType */
export class TaskDetailsWindowsMessage extends BaseMessage {
    private pid: number;
    public response?: ITaskDetailsWinResponse;

    constructor(pid?: number) {
        super();
        this.header.command = MessageType.PC_GETTHRANDMODLISTS;
        this.pid = pid;
    }

    public serializeBody(buffer: MemoryBuffer): void {
        buffer.writeUInt32BE(this.pid);
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        if (this.header.errorCode != 0) {
            return;
        }

        let taskThreads: TaskThread[] = [];
        let jsonArrayModule: TaskModule[] = [];
        const numOfThreads = buffer.readUInt32BE();
        const sizeOfThreadList = buffer.readUInt32BE();

        for (var i = 0; i < numOfThreads; i++) {
            let taskThread: TaskThread = new TaskThread();
            taskThread.tid = buffer.readUInt32LE();
            taskThread.pri = buffer.readUInt32LE();
            taskThreads.push(taskThread);
            // console.log("tid: " + json.tid + "; pri: " + json.pri);
        }

        const numOfModules = buffer.readUInt32BE();;
        const sizeOfModuleList = buffer.readUInt32BE();
        const count = (buffer.getByteArray().length - buffer.getPosition());
        if (count === sizeOfModuleList) {
            for (let i = 0; i < numOfModules; i++) {
                let taskModule = new TaskModule();
                taskModule.baseAdd = buffer.readUInt32LE();
                taskModule.baseSize = buffer.readUInt32LE();
                taskModule.usage = buffer.readUInt32LE();
                taskModule.procUsage = buffer.readUInt32LE();
                taskModule.handle = buffer.readUInt32LE();
                taskModule.name = buffer.readCharArray(132);
                jsonArrayModule.push(taskModule);
            }
        }
        this.response = {
            threads: taskThreads,
            modules: jsonArrayModule
        };
    }
}

/** PC_KILLTSK MessageType */
export class KillWindowsProcessMessage extends BaseMessage {
    private pid: number;
    constructor(pid: number) {
        super();
        this.header.command = MessageType.PC_KILLTSK;
        this.pid = pid;
    }

    public serializeBody(buffer: MemoryBuffer): void {
        buffer.writeUInt32BE(this.pid);
    }
}

export class TaskWindowsTimes {
    create: number;
    kernel: number;
    user: number;
}

/** PC_GETTHREADTIMES MessageType */
export class ThreadTimesWindowsMessage extends BaseMessage {
    public response?: Map<number, Map<number,TaskWindowsTimes>>;

    constructor() {
        super();
        this.header.command = MessageType.PC_GETTHREADTIMES;
    }

    public serializeBody(buffer: MemoryBuffer): void {
        // if (retReqId !== undefined) {
        //     retReqId[0] = reqId;
        // }
        buffer.writeUInt8(2);
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        if (this.header.errorCode != 0) {
            return;
        }

        this.response = new Map<number, Map<number, TaskWindowsTimes>>();
        const count = (buffer.getByteArray().length - buffer.getPosition()) / (5 * 4); // 5 DWORDS per PROCESS_TIME
        for (let i = 0; i < count; i++) {
            const pid = buffer.readUInt32BE();
            let pidMap = this.response.get(pid);
            if (!pidMap) {
                pidMap = new Map<number, TaskWindowsTimes>();
                this.response.set(pid, pidMap);
            }
            const tid = buffer.readUInt32BE();
            const create = buffer.readUInt32BE();
            const kernel = buffer.readUInt32BE();
            const user = buffer.readUInt32BE();
            if (!pidMap.get(tid)) {
                let info = new TaskWindowsTimes();
                info.create = create;
                info.kernel = kernel;
                info.user = user;
                pidMap.set(tid, info);
            }
        }
    }
}

export class TaskWindowsInfo implements hd.IRcTask {
    pid: number;
    // dwInheritedFromProcessId: number;
    // cntThreads: number;
    // th32ModuleID: number;
    // th32DefaultHeapID: number;
    // pcPriClassBase: number;
    // flags: number;
    // hwnd: number;
    name: string;
    type: string;
}

/** PC_GETTSKLIST MessageType */
export class TaskListWindowsMessage extends BaseMessage {
    public response?: hd.IRcTask[];

    constructor() {
        super();
        this.header.command = MessageType.PC_GETTSKLIST;
    }

    // message has no custom body content, so serializeBody() override

    public deserializeBody(buffer: MemoryBuffer): void {
        if (this.header.errorCode != 0) {
            return;
        }
        const numOfProcesses = buffer.readUInt32BE();
        const bytesLeft = buffer.readUInt32BE();

        var count = (buffer.getByteArray().length - buffer.getPosition());
        if (count === bytesLeft) {
            this.response = [];

            for (let i = 0; i < numOfProcesses; i++) {
                let twi = new TaskWindowsInfo();
                const pos = buffer.getPosition();
                const cntUsage = buffer.readUInt32LE();
                twi.pid = buffer.readUInt32LE();
                const dwInheritedFromProcessId = buffer.readUInt32LE();
                const cntThreads = buffer.readUInt32LE();
                const th32ModuleID = buffer.readUInt32LE();
                const th32DefaultHeapID = buffer.readUInt32LE();
                const pcPriClassBase = buffer.readUInt32LE();
                const flags = buffer.readUInt32LE();
                const hwnd = buffer.readUInt32LE();
                twi.name = buffer.readCharArray(128);
                twi.type = buffer.readCharArray(128);
                this.response.push(twi);
            }
        }
    }
}
